import { useInfiniteQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";

import { getItemFlowRunsInfiniteQueryOptions } from "@/modules/flow";
import type { Item } from "@/modules/mine/types";
import type { SheetComponentProps } from "@/shared/components/sheet";
import { buildMineWorkflowPath } from "@/shared/constants/route-paths";
import {
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/shared/ui/sheet";

import { FlowRunsList } from "./flow-runs-list";

interface ItemFlowRunsSheetProps extends SheetComponentProps {
  item: Item;
}

export function ItemFlowRunsSheet({
  close,
  item,
}: ItemFlowRunsSheetProps) {
  const navigate = useNavigate();
  const runsQuery = useInfiniteQuery(
    getItemFlowRunsInfiniteQueryOptions(item.id, true),
  );
  const runs = runsQuery.data?.pages.flatMap((page) => page.runs) ?? [];

  return (
    <>
      <SheetHeader className="border-b px-6 py-5">
        <SheetTitle className="pr-8 text-base font-semibold tracking-tight">
          流程记录
        </SheetTitle>
        <SheetDescription className="sr-only">
          {item.title || "未命名收藏"}的流程执行记录
        </SheetDescription>
      </SheetHeader>

      <div className="min-h-0 flex-1 overflow-y-auto p-6">
        <p className="mb-4 truncate text-xs text-muted-foreground">
          {item.title || "未命名收藏"}
          {runsQuery.isPending ? null : ` · ${runs.length} 条记录`}
        </p>
        <FlowRunsList
          detailMode="inline"
          emptyDescription="可能没有流程匹配这个收藏，不代表执行异常。"
          hasNextPage={runsQuery.hasNextPage}
          isError={runsQuery.isError}
          isFetchingNextPage={runsQuery.isFetchingNextPage}
          isPending={runsQuery.isPending}
          itemTitle={item.title}
          onLoadMore={() => void runsQuery.fetchNextPage()}
          onRetry={() => void runsQuery.refetch()}
          onRuleClick={(flowRuleId) => {
            close();
            navigate(buildMineWorkflowPath(flowRuleId));
          }}
          runs={runs}
          showRule
        />
      </div>
    </>
  );
}
